import React, { useState } from "react";
import InfoToRelease from "./InfoToRelease";
import { Release, ReleaseInfoUpdate } from "../../../models/transaction";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";
import { infoToReleaseValidiation } from "./validations/localValidations";

const PATIENT_MIN_DOB = new Date(1900, 0, 1);

export interface RecordsRequestedFieldProps {
    /** props.transactionDetails.releaseInfo - same source both classic and Quick Entry read. */
    readonly releaseInfo: readonly Release[];
    readonly requestComments?: string;
    readonly organizationId?: number;
    readonly requestBufferId?: number;
    readonly dob?: Date;
    /** Hides the field entirely - classic pane's isVisibleSensitiveInfoToRelease gate. */
    readonly visible: boolean;
    readonly saveInformationToRelease?: (data: ReleaseInfoUpdate) => void;
}

/**
 * Shared Records Requested + Service Date field (Spec #34098 / F1a). Unlike
 * the other 4 wired fields, this one is NOT a redux-form field - it's
 * backed by component-local state seeded from transactionDetails.releaseInfo
 * and persisted via an immediate API call (saveInformationToRelease), the
 * exact pattern DataEntryDetailsRightPane.tsx used inline before this
 * extraction. Wraps the existing InfoToRelease.tsx (date range + records
 * checkbox list + free text) rather than reinventing it - both classic
 * RightPane and Quick Entry mount this same component now.
 */
const RecordsRequestedField: React.FC<RecordsRequestedFieldProps> = (props) => {
    const [informationRelease, setInformationRelease] = useState(props.releaseInfo);
    const [requestComments, setRequestComments] = useState(props.requestComments);
    const [infoReleaseInitialLoad, setInfoReleaseInitialLoad] = useState(true);
    const [infoReleaseErrors, setInfoReleaseErrors] = useState(infoToReleaseValidiation(informationRelease, props.dob).errors);

    // Forces InfoToRelease (whose own useState only seeds once from props.data)
    // to re-initialize when the underlying release item set itself changes -
    // same key-based remount trick the classic pane already relies on.
    const releaseItemsKey = props.releaseInfo.map(item => item.id).sort().join(",");

    if (!props.visible) {
        return null;
    }

    const validateReleaseInformation = (release: readonly Release[]) => {
        setInfoReleaseInitialLoad(false);
        const infoErrors = infoToReleaseValidiation(release, props.dob);
        setInfoReleaseErrors(infoErrors.errors);
        return !infoErrors.display_error;
    };

    const saveRelease = (updatedValues: readonly Release[], from: Date | undefined, to: Date | undefined, comments: string | undefined) => {
        if (!validateReleaseInformation(updatedValues)) {
            return;
        }
        props.saveInformationToRelease?.({
            organizationId: props.organizationId ?? 0,
            requestBufferId: props.requestBufferId ?? 0,
            releases: updatedValues.filter(item => item.checked).map(item => item.id),
            from,
            to,
            requestComments: comments,
        });
    };

    const onChangeItem = (itemId: number, checked: boolean, from: Date | undefined, to: Date | undefined, all: boolean) => {
        const updatedValues = informationRelease.map(info => info.id === itemId ? {
            ...info,
            checked,
            dateOfService: from && to && !all ? { from, to } : undefined,
            allDates: all,
        } : info);
        setInformationRelease(updatedValues);
        saveRelease(updatedValues, updatedValues.find(val => val.checked)?.dateOfService?.from, updatedValues.find(val => val.checked)?.dateOfService?.to, requestComments);
    };

    const onChangeDates = (from: Date | undefined, to: Date | undefined, all: boolean) => {
        const updatedValues: readonly Release[] = informationRelease.map(info => ({
            ...info,
            dateOfService: from && to && !all ? { from, to } : undefined,
            allDates: all,
        }));
        setInformationRelease(updatedValues);
        saveRelease(updatedValues, all ? undefined : from, all ? undefined : to, requestComments);
    };

    const onChangeFreeText = (text: string) => {
        setRequestComments(text);
        saveRelease(informationRelease, informationRelease.find(val => val.checked)?.dateOfService?.from, informationRelease.find(val => val.checked)?.dateOfService?.to, text);
    };

    return (
        <InfoToRelease
            key={releaseItemsKey}
            data={props.releaseInfo}
            onChangeItem={onChangeItem}
            onChangeDates={onChangeDates}
            onChangeFreeText={onChangeFreeText}
            freeText={requestComments}
            fromErrorMsg={infoReleaseErrors.from}
            toErrorMsg={infoReleaseErrors.to}
            errorMsg={infoReleaseErrors.items}
            initialLoad={infoReleaseInitialLoad}
            minDate={props.dob ?? PATIENT_MIN_DOB}
            maxDate={new Date()}
        />
    );
};

export default RecordsRequestedField;

export const RECORDS_REQUESTED_ROW_ID = "recordsRequested";

/**
 * Builds the FieldGroupRow for the Records Requested row. Confirmed once at
 * least one record type is checked - date-range requiredness now lives on
 * the separate Service Date-From/To rows below (master-demo-v6.html shows
 * these as 3 independent rows; classic mode keeps its own combined
 * InfoToRelease UI/validation untouched - this mapper only feeds Quick
 * Entry's row list).
 */
export function mapRecordsRequestedToFieldGroupRow(badgeNumber: number, releaseInfo: readonly Release[]): FieldGroupRow {
    const checkedItems = releaseInfo.filter(item => item.checked);
    const hasValue = checkedItems.length > 0;

    return {
        id: RECORDS_REQUESTED_ROW_ID,
        badgeNumber,
        status: hasValue ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.NeedsEntry,
        label: "Records Requested",
        value: hasValue ? checkedItems.map(item => item.name).join(", ") : null,
        isAiPreSelected: false,
    };
}

/**
 * dateOfService.from/to are typed as Date (models/transaction.ts's
 * DateRange) but a value straight off the API's JSON response is really an
 * ISO string until something parses it - nothing upstream of this file
 * does that for releaseInfo, so calling a Date-only method (e.g. this
 * file's own formatServiceDate) on an unparsed value throws at runtime
 * ("date.toLocaleDateString is not a function", direct user report).
 * Coerced once here so every consumer of getReleaseDateRange gets a real
 * Date or undefined, never a raw string.
 */
function coerceDate(value: Date | string | undefined): Date | undefined {
    if (!value) {
        return undefined;
    }
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? undefined : date;
}

/**
 * The one date range shared uniformly across every release item (mirrors
 * InfoToRelease.tsx's own invariant: a single From/To/AllDates applies to
 * whichever records are checked). Quick Entry's split Service Date-From/To
 * rows read this directly instead of going through InfoToRelease's internal
 * scratch state.
 */
export function getReleaseDateRange(releaseInfo: readonly Release[]): { from?: Date; to?: Date; allDates: boolean } {
    const allDates = releaseInfo.some(item => item.allDates);
    const dated = releaseInfo.find(item => item.dateOfService);
    return { from: coerceDate(dated?.dateOfService?.from), to: coerceDate(dated?.dateOfService?.to), allDates };
}

/**
 * Toggles one item's checked state for Quick Entry's Records Requested row.
 * Never needs to touch dateOfService itself - buildReleaseInfoWithDateRange
 * below always writes the shared date range onto every item (checked or
 * not), so a newly-checked item already carries whatever range was picked,
 * even if it was picked before anything was checked.
 */
export function buildReleaseInfoWithItemToggle(releaseInfo: readonly Release[], itemId: number, checked: boolean): readonly Release[] {
    return releaseInfo.map(info => info.id === itemId ? { ...info, checked } : info);
}

/** Applies a new date range to every item uniformly - Quick Entry's Service Date-From/To rows. */
export function buildReleaseInfoWithDateRange(
    releaseInfo: readonly Release[],
    from: Date | undefined,
    to: Date | undefined,
    allDates: boolean,
): readonly Release[] {
    return releaseInfo.map(info => ({
        ...info,
        dateOfService: from && to && !allDates ? { from, to } : undefined,
        allDates,
    }));
}

function formatServiceDate(date: Date): string {
    return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

export const SERVICE_DATE_FROM_ROW_ID = "serviceDateFrom";
export const SERVICE_DATE_TO_ROW_ID = "serviceDateTo";

/**
 * Takes the {from, to, allDates} range directly rather than releaseInfo -
 * Quick Entry's own scratch date state (DataEntryDetails.tsx's
 * quickEntryServiceDateOverride) is the source of truth for what the
 * specialist currently has entered, since a half-complete range (e.g. only
 * From set) has nowhere to live in releaseInfo's own AND-gated
 * dateOfService representation. Each row's Confirmed status is
 * independent of the other - From doesn't wait on To.
 */
export function mapServiceDateFromToFieldGroupRow(badgeNumber: number, dateRange: { from?: Date; to?: Date; allDates: boolean }): FieldGroupRow {
    const { from, allDates } = dateRange;
    const hasValue = allDates || Boolean(from);

    return {
        id: SERVICE_DATE_FROM_ROW_ID,
        badgeNumber,
        status: hasValue ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.NeedsEntry,
        label: "Service Date - From",
        value: allDates ? "All Dates" : (from ? formatServiceDate(from) : null),
        isAiPreSelected: false,
    };
}

export function mapServiceDateToToFieldGroupRow(badgeNumber: number, dateRange: { from?: Date; to?: Date; allDates: boolean }): FieldGroupRow {
    const { to, allDates } = dateRange;
    const hasValue = allDates || Boolean(to);

    return {
        id: SERVICE_DATE_TO_ROW_ID,
        badgeNumber,
        status: hasValue ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.NeedsEntry,
        label: "Service Date - To",
        value: allDates ? "All Dates" : (to ? formatServiceDate(to) : null),
        isAiPreSelected: false,
    };
}
